import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ThemeService } from '../../services/theme.service';
import { I18nService } from '../../services/i18n.service';
import { LOCALE_META, LOCALES, Locale } from '../../types/i18n.types';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'ds-site-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UpperCasePipe, NgTemplateOutlet, RouterLink],
  template: `
    <header class="ds-header" [class.ds-header--scrolled]="isScrolled()">
      <div class="ds-header__inner">
        <!-- Logo -->
        <a routerLink="/" class="ds-header__logo" aria-label="DemandSens — accueil">
          <span class="ds-header__logo-mark" aria-hidden="true">
            <img
              src="demandsens-picto.png"
              alt=""
              width="32"
              height="32"
              class="ds-header__logo-img ds-header__logo-img--light"
            />
            <img
              src="demandsens-picto-dark.png"
              alt=""
              width="32"
              height="32"
              class="ds-header__logo-img ds-header__logo-img--dark"
            />
          </span>
          <span class="ds-header__logo-text">DemandSens</span>
        </a>

        <!-- Nav -->
        @if (h(); as h) {
          <nav class="ds-header__nav">
            @for (link of h.nav; track link; let i = $index) {
              @let href = h.nav_href[i];
              @if (href) {
                <a
                  [routerLink]="href"
                  class="ds-header__nav-item ds-header__nav-item--link"
                  [class.ds-header__nav-item--active]="activeIndex() === i"
                  (mouseenter)="i === 0 && openMega()"
                  (mouseleave)="i === 0 && closeMega()"
                  >{{ link }}</a
                >
              } @else {
                <span
                  class="ds-header__nav-item"
                  [class.ds-header__nav-item--active]="activeIndex() === i"
                  (mouseenter)="i === 0 && openMega()"
                  (mouseleave)="i === 0 && closeMega()"
                  >{{ link }}</span
                >
              }
            }
          </nav>

          <!-- Right -->
          <div class="ds-header__right">
            <button
              class="ds-header__theme-btn"
              (click)="toggleTheme()"
              [attr.aria-label]="isDark() ? h.theme_to_light : h.theme_to_dark"
            >
              @if (isDark()) {
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              } @else {
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  aria-hidden="true"
                >
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              }
            </button>

            <!-- Language picker -->
            <div class="ds-lang">
              <button
                type="button"
                class="ds-lang__btn"
                [class.ds-lang__btn--open]="langOpen()"
                (click)="toggleLang($event)"
                [attr.aria-haspopup]="'listbox'"
                [attr.aria-expanded]="langOpen()"
                [attr.aria-label]="'Language: ' + currentMeta().label"
              >
                <span class="ds-lang__flag" aria-hidden="true">
                  <ng-container
                    *ngTemplateOutlet="flag; context: { $implicit: currentMeta().region }"
                  />
                </span>
                <span class="ds-lang__code">{{ currentMeta().code | uppercase }}</span>
                <svg
                  class="ds-lang__caret"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  aria-hidden="true"
                >
                  <path
                    d="M2,4 L5,7 L8,4"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              @if (langOpen()) {
                <ul class="ds-lang__menu" role="listbox">
                  @for (l of locales; track l) {
                    @let m = meta(l);
                    <li role="option" [attr.aria-selected]="l === currentLocale()">
                      <button
                        type="button"
                        class="ds-lang__opt"
                        [class.ds-lang__opt--active]="l === currentLocale()"
                        (click)="pickLocale(l)"
                      >
                        <span class="ds-lang__flag" aria-hidden="true">
                          <ng-container
                            *ngTemplateOutlet="flag; context: { $implicit: m.region }"
                          />
                        </span>
                        <span class="ds-lang__opt-label">{{ m.label }}</span>
                        @if (l === currentLocale()) {
                          <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
                            <path
                              d="M3,8 L6.5,11.5 L13,5"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        }
                      </button>
                    </li>
                  }
                </ul>
              }
            </div>

            <a class="ds-header__cta" [href]="demoUrl" target="_blank" rel="noopener noreferrer">
              {{ h.cta }}
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                aria-hidden="true"
              >
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </a>
          </div>
        }
      </div>

      <!-- Flag SVG template (FR / GB / DE) -->
      <ng-template #flag let-region>
        @switch (region) {
          @case ('FR') {
            <svg viewBox="0 0 90 60" preserveAspectRatio="none">
              <rect width="30" height="60" fill="#0055A4" />
              <rect x="30" width="30" height="60" fill="#fff" />
              <rect x="60" width="30" height="60" fill="#EF4135" />
            </svg>
          }
          @case ('DE') {
            <svg viewBox="0 0 100 60" preserveAspectRatio="none">
              <rect width="100" height="20" fill="#000" />
              <rect y="20" width="100" height="20" fill="#DD0000" />
              <rect y="40" width="100" height="20" fill="#FFCE00" />
            </svg>
          }
          @case ('GB') {
            <svg viewBox="0 0 60 30" preserveAspectRatio="none">
              <rect width="60" height="30" fill="#012169" />
              <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6" />
              <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" stroke-width="3" />
              <path d="M30,0 V30 M0,15 H60" stroke="#fff" stroke-width="10" />
              <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" stroke-width="6" />
            </svg>
          }
          @case ('IT') {
            <svg viewBox="0 0 90 60" preserveAspectRatio="none">
              <rect width="30" height="60" fill="#008C45" />
              <rect x="30" width="30" height="60" fill="#F4F5F0" />
              <rect x="60" width="30" height="60" fill="#CD212A" />
            </svg>
          }
        }
      </ng-template>

      <!-- Mega menu — Produit -->
      @if (megaOpen() && h(); as h) {
        <div class="ds-header__mega" (mouseenter)="openMega()" (mouseleave)="closeMega()">
          <div class="ds-header__mega-inner">
            @for (col of h.mega; track col.eye) {
              <div class="ds-header__mega-col">
                @if (col.href) {
                  <a
                    [routerLink]="col.href"
                    class="ds-header__mega-eyebrow ds-header__mega-eyebrow--link"
                    >{{ col.eye }}</a
                  >
                } @else {
                  <div class="ds-header__mega-eyebrow">{{ col.eye }}</div>
                }
                @for (item of col.items; track item.title) {
                  @if (item.href) {
                    <a [routerLink]="item.href" class="ds-header__mega-item">
                      <div class="ds-header__mega-item-title">{{ item.title }}</div>
                      <div class="ds-header__mega-item-desc">{{ item.desc }}</div>
                    </a>
                  } @else {
                    <div
                      class="ds-header__mega-item ds-header__mega-item--disabled"
                      aria-disabled="true"
                    >
                      <div class="ds-header__mega-item-title">{{ item.title }}</div>
                      <div class="ds-header__mega-item-desc">{{ item.desc }}</div>
                    </div>
                  }
                }
              </div>
            }
          </div>
        </div>
      }
    </header>
  `,
  styles: [
    `
      .ds-header {
        position: sticky;
        top: 0;
        z-index: 100;
        font-family: var(--f-display);
        background: var(--surface);
        border-bottom: 1px solid var(--border-subtle);
        padding: 20px 64px;
        transition:
          padding 200ms var(--e-standard),
          background 200ms var(--e-standard);

        @media (max-width: 768px) {
          padding: 16px 24px;
        }
      }

      .ds-header__inner {
        max-width: 1280px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 32px;
      }

      .ds-header--scrolled {
        padding: 14px 64px;
        background: color-mix(in srgb, var(--surface) 94%, transparent);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border-bottom-color: var(--border);

        @media (max-width: 768px) {
          padding: 10px 24px;
        }
      }

      /* ── Logo ─────────────────────────────────────────────────── */
      .ds-header__logo {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        text-decoration: none;
        font-size: 17px;
        font-weight: 700;
        letter-spacing: -0.01em;
        color: var(--on-surface);
        flex-shrink: 0;

        &:focus-visible {
          outline: 2px solid var(--c-focus);
          outline-offset: 2px;
          border-radius: 4px;
        }
      }

      .ds-header__logo-mark {
        width: 32px;
        height: 32px;
        display: inline-grid;
        place-items: center;
        flex-shrink: 0;
        position: relative;
      }
      .ds-header__logo-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
      }
      .ds-header__logo-img--dark {
        display: none;
      }
      :host-context(.dark) .ds-header__logo-img--light {
        display: none;
      }
      :host-context(.dark) .ds-header__logo-img--dark {
        display: block;
      }
      .ds-header__logo-text {
        line-height: 1;
      }

      /* ── Nav ──────────────────────────────────────────────────── */
      .ds-header__nav {
        display: flex;
        gap: 32px;
        font-size: 14px;
        font-weight: 700;
      }

      .ds-header__nav-item {
        color: var(--on-surface);
        cursor: pointer;
        position: relative;
        padding-bottom: 4px;
        border-bottom: 2px solid transparent;
        transition: border-color 150ms var(--e-standard);
        white-space: nowrap;
        font: inherit;
        text-decoration: none;

        &--active {
          border-bottom-color: var(--c-accent);
        }
        &:hover {
          border-bottom-color: var(--border);
        }
        &--link:focus-visible {
          outline: 2px solid var(--c-focus);
          outline-offset: 2px;
          border-radius: 2px;
        }
      }

      /* ── Right ────────────────────────────────────────────────── */
      .ds-header__right {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .ds-header__theme-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        padding: 6px;
        border-radius: 6px;
        cursor: pointer;
        color: var(--on-surface-muted);
        transition:
          background-color var(--d-2) var(--e-standard),
          color var(--d-2) var(--e-standard);

        &:hover {
          background: var(--surface-secondary);
          color: var(--on-surface);
        }
        &:focus-visible {
          outline: 2px solid var(--c-focus);
          outline-offset: 2px;
        }
      }

      /* ── Language picker ──────────────────────────────────────── */
      .ds-lang {
        position: relative;
      }
      .ds-lang__btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: transparent;
        border: none;
        padding: 6px 8px;
        border-radius: 6px;
        cursor: pointer;
        color: var(--on-surface-muted);
        font-family: var(--f-mono);
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.04em;
        transition:
          background-color var(--d-2) var(--e-standard),
          color var(--d-2) var(--e-standard);

        &:hover,
        &--open {
          background: var(--surface-secondary);
          color: var(--on-surface);
        }
        &:focus-visible {
          outline: 2px solid var(--c-focus);
          outline-offset: 2px;
        }
      }
      .ds-lang__flag {
        width: 18px;
        height: 13px;
        display: inline-flex;
        border-radius: 2px;
        overflow: hidden;
        box-shadow: 0 0 0 1px color-mix(in srgb, currentColor 18%, transparent);

        svg {
          width: 100%;
          height: 100%;
          display: block;
        }
      }
      .ds-lang__code {
        line-height: 1;
      }
      .ds-lang__caret {
        transition: transform var(--d-2) var(--e-standard);
        .ds-lang__btn--open & {
          transform: rotate(180deg);
        }
      }
      .ds-lang__menu {
        position: absolute;
        top: calc(100% + 6px);
        right: 0;
        list-style: none;
        margin: 0;
        padding: 6px;
        min-width: 180px;
        background: var(--surface);
        border: 1px solid var(--border);
        border-radius: 8px;
        box-shadow: var(--sh-3);
        z-index: 50;
      }
      .ds-lang__opt {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 10px;
        background: transparent;
        border: none;
        cursor: pointer;
        border-radius: 6px;
        font-family: var(--f-body);
        font-size: 13px;
        color: var(--on-surface);
        text-align: left;
        transition: background-color var(--d-2) var(--e-standard);

        &:hover {
          background: var(--surface-secondary);
        }
        &:focus-visible {
          outline: 2px solid var(--c-focus);
          outline-offset: 2px;
        }
        &--active {
          color: var(--c-accent);
          font-weight: 600;
        }
      }
      .ds-lang__opt-label {
        flex: 1;
      }

      .ds-header__cta {
        background: var(--c-accent);
        color: var(--c-accent-ink);
        border: none;
        padding: 8px 16px;
        border-radius: var(--r-sm);
        font-family: var(--f-body);
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        min-height: 36px;
        text-decoration: none;
        box-sizing: border-box;
        transition:
          background-color var(--d-2) var(--e-standard),
          transform var(--d-2) var(--e-decel),
          box-shadow var(--d-2) var(--e-decel);

        &:hover {
          background-color: var(--c-accent-hover);
          transform: translateY(-1px);
          box-shadow: var(--sh-2);
        }
        &:active {
          transform: translateY(0);
          box-shadow: none;
          transition-duration: 80ms;
        }
        &:focus-visible {
          outline: 2px solid var(--c-focus);
          outline-offset: 2px;
        }
      }

      /* ── Mega menu ────────────────────────────────────────────── */
      .ds-header__mega {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: var(--surface);
        border-bottom: 1px solid var(--border);
        box-shadow: var(--sh-3);
        z-index: 9;
        padding: 40px 64px;
      }

      .ds-header__mega-inner {
        max-width: 1280px;
        margin: 0 auto;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 48px;
      }

      .ds-header__mega-col {
        display: flex;
        flex-direction: column;
      }

      .ds-header__mega-eyebrow {
        font-family: var(--f-mono);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #cc2831;
        margin-bottom: 18px;
        text-decoration: none;
        display: block;

        :where(.dark) & {
          color: #ff6b6b;
        }

        &--link {
          transition: opacity 150ms var(--e-standard);
          cursor: pointer;
          &:hover {
            opacity: 0.72;
          }
          &:focus-visible {
            outline: 2px solid var(--c-focus);
            outline-offset: 2px;
            border-radius: 2px;
          }
        }
      }

      .ds-header__mega-item {
        display: block;
        padding: 12px 0;
        border-bottom: 1px solid var(--border-subtle);
        color: var(--on-surface);
        text-decoration: none;
        cursor: pointer;
        transition: opacity 150ms var(--e-standard);

        &:hover {
          opacity: 0.72;
        }
        &:focus-visible {
          outline: 2px solid var(--c-focus);
          outline-offset: 2px;
          border-radius: 2px;
        }

        &--disabled {
          cursor: default;
          opacity: 0.5;
          &:hover {
            opacity: 0.5;
          }
        }
      }

      .ds-header__mega-item-title {
        font-size: 16px;
        font-weight: 700;
        letter-spacing: -0.01em;
      }

      .ds-header__mega-item-desc {
        font-family: var(--f-body);
        font-size: 13px;
        color: var(--on-surface-muted);
        margin-top: 2px;
      }
    `,
  ],
})
export class DsSiteHeaderComponent {
  private readonly themeService = inject(ThemeService);
  private readonly i18n = inject(I18nService);
  private readonly elRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly activeIndex = input<number | undefined>(undefined);

  readonly isDark = this.themeService.isDark;
  readonly megaOpen = signal(false);
  readonly isScrolled = signal(false);
  readonly langOpen = signal(false);

  readonly h = computed(() => this.i18n.translations()?.header ?? null);

  readonly locales = LOCALES;
  readonly currentLocale = this.i18n.locale;
  readonly currentMeta = computed(() => LOCALE_META[this.currentLocale()]);
  readonly demoUrl = environment.BookDemo;

  meta(l: Locale) {
    return LOCALE_META[l];
  }

  constructor() {
    afterNextRender(() => {
      window.addEventListener(
        'scroll',
        () => {
          this.isScrolled.set(window.scrollY > 20);
        },
        { passive: true },
      );

      document.addEventListener('click', (ev) => {
        if (!this.langOpen()) return;
        if (!this.elRef.nativeElement.contains(ev.target as Node)) {
          this.langOpen.set(false);
        }
      });

      document.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape' && this.langOpen()) this.langOpen.set(false);
      });
    });
  }

  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly CLOSE_DELAY_MS = 150;

  openMega(): void {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.megaOpen.set(true);
  }

  closeMega(): void {
    if (this.closeTimer !== null) clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.megaOpen.set(false);
      this.closeTimer = null;
    }, this.CLOSE_DELAY_MS);
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  toggleLang(ev: Event): void {
    ev.stopPropagation();
    this.langOpen.update((v) => !v);
  }
  pickLocale(l: Locale): void {
    this.i18n.setLocale(l);
    this.langOpen.set(false);
  }
}
